import React, { useState } from 'react';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import { Grid, Box, Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ModalBusyContainer from 'components/ModalBusyContainer';
import CustomUpload from 'components/Forms/Upload';
import { useTranslation } from 'react-i18next';
import { FORM_ERROR } from 'final-form';
import { resolveApiErrorMessage } from 'utils';
import validate from './validation';

const useStyles = makeStyles(() => ({
  btnContainer: {
    marginTop: '30px',
  },
  btn: {
    minWidth: '140px',
  },
}));

const TEN_MB_IN_BYTES = 10 * 1024 * 1024;

const buildImportFieldValidationMessage = ({ row, column, errorValue }) => {
  const normalizedErrorValue = typeof errorValue === 'string' ? errorValue.trim() : '';

  if (!normalizedErrorValue) {
    return i18next.t('errors.importError', {
      row,
      column,
    });
  }

  const translatedError = i18next.t(`errors.${normalizedErrorValue}`);

  if (translatedError !== `errors.${normalizedErrorValue}`) {
    return i18next.t('errors.importFieldWithValidationError', {
      row,
      column,
      error: translatedError,
    });
  }

  return i18next.t('errors.importFieldValueNotFound', {
    row,
    column,
    value: normalizedErrorValue,
  });
};

export const getImportErrorMessage = (message, formName) => {
  let errorMessage;
  if (typeof message === 'string') {
    const rowError = message.split(`file.${formName}.row.`)[1] || message.split('file.users.row.')[1];
    const importError =
      message.split(`file.${formName}.errorInImportStructure.`)[1] ||
      message.split('file.users.errorInImportStructure.')[1];
    const unknownType = message === 'expirationMonth.unknownType';
    if (unknownType) {
      return i18next.t(`errors.expirationMonthUnknown`);
    }
    const combinedError = rowError || importError;
    if (combinedError) {
      const errorParts = combinedError.split('.');
      const row = errorParts[0];
      const column = errorParts[1];
      const errorValue = errorParts.slice(2).join('.');

      if (rowError) {
        errorMessage = i18next.t('errors.importError', {
          row,
          column,
        });
      } else if (importError) {
        errorMessage = i18next.t('errors.importErrorStructure', {
          row,
          column,
        });
      }
      if (errorValue) {
        errorMessage = buildImportFieldValidationMessage({
          row,
          column,
          errorValue,
        });
      }
    }
  }
  return errorMessage;
};

const SendFileForm = ({
  onSubmitForm,
  submitting: formSubmitting,
  fileFormat,
  maxFileSize,
  confirmTitle,
  rejectTitle,
  rejectAction,
  formType,
  formName,
  fieldName,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const [file, setFile] = useState();
  const submitButtonTitle = confirmTitle || (formType === 'import' ? 'buttons.import' : 'buttons.save');

  const handleSubmitForm = async () => {
    let responseData;

    try {
      responseData = await onSubmitForm({
        [fieldName || 'file']: file,
      });
    } catch (error) {
      responseData = {
        error: true,
        message: error?.message || ['errors.serverError'],
      };
    }

    const errors = {};

    if (!responseData) {
      errors[FORM_ERROR] = t('errors.serverError');
      return errors;
    }

    if (responseData.error) {
      if (formType === 'import') {
        const errorMessage = getImportErrorMessage(responseData.message, formName);
        if (errorMessage) {
          errors[FORM_ERROR] = errorMessage;
        } else {
          errors[FORM_ERROR] = resolveApiErrorMessage(responseData.message, {
            t,
            fallbackMessage: t('errors.serverError'),
          });
        }
      } else {
        errors[FORM_ERROR] = resolveApiErrorMessage(responseData.message, {
          t,
          fallbackMessage: t('errors.serverError'),
        });
      }
    }
    return errors;
  };

  const isLoading = formSubmitting;

  return (
    <Form
      onSubmit={handleSubmitForm}
      validate={validate}
      initialValues={{ file }}
      render={({ handleSubmit, submitting, form, submitError }) => {
        return (
          <form onSubmit={handleSubmit}>
            <ModalBusyContainer busy={isLoading}>
              <Box width="100%" mt={5}>
                <Field
                  fullWidth
                  id="file"
                  name={fieldName || 'file'}
                  disabled={submitting}
                  onChange={setFile}
                  onDelete={() => form.reset()}
                  component={CustomUpload}
                  accept={fileFormat}
                  multiple={false}
                  maxSize={maxFileSize}
                />
              </Box>
              {submitError && <Typography color="error">{submitError}</Typography>}
              <Grid container spacing={3} className={classes.btnContainer}>
                <Grid item xs={6}>
                  <Box textAlign="right">
                    <Button
                      className={classes.btn}
                      type="submit"
                      color="primary"
                      variant="contained"
                      disabled={submitting || (file !== undefined && file.length === 0)}
                      size="large"
                    >
                      {t(submitButtonTitle)}
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="left">
                    <Button
                      className={classes.btn}
                      variant="outlined"
                      size="large"
                      type="reset"
                      disabled={submitting}
                      onClick={rejectAction}
                    >
                      {t(rejectTitle)}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </ModalBusyContainer>
          </form>
        );
      }}
    />
  );
};

SendFileForm.defaultProps = {
  maxFileSize: TEN_MB_IN_BYTES,
  confirmTitle: undefined,
  rejectTitle: 'buttons.cancel',
  rejectAction: () => {},
  formType: undefined,
  formName: undefined,
  fieldName: undefined,
};

SendFileForm.propTypes = {
  onSubmitForm: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  fileFormat: PropTypes.string.isRequired,
  maxFileSize: PropTypes.number,
  confirmTitle: PropTypes.string,
  rejectTitle: PropTypes.string,
  rejectAction: PropTypes.func,
  formType: PropTypes.string,
  formName: PropTypes.string,
  fieldName: PropTypes.string,
};

export default SendFileForm;
